import React, { useEffect, useMemo, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Film, Plus, Upload, LayoutGrid, List, ChevronDown, ChevronRight, Calendar, ArrowUpDown, Filter } from "lucide-react";
import { toast } from "sonner";
import { compareProjectStatuses, getProjectStatusTone, normalizeProjectStatus } from "../lib/projectStatus";
import { generateProjectShowColour, getReadableTextColour, normalizeHexColour } from "../lib/projectShowColours";
import { formatCurrency, cn, getCompanyLogoUrl } from "../utils";

interface ProjectSummary {
  id: string;
  projectName: string;
  clientName: string;
  distributorName?: string;
  createdAt: string;
  bidDate: string;
  endDate?: string;
  ownerId: string;
  thumbnail?: string;
  latestVersionName?: string;
  latestTotalCost?: number;
  currency?: string;
  status: string;
  themeAccentColor?: string;
  themeAccentColorSource?: string;
}

interface Props {
  onSelectProject: (projectId: string) => void;
  onNewProject: () => void;
}

const DASHBOARD_PROJECTS_CACHE_KEY = "dashboard_projects_summary_v1";

function normalizeProjectSummary(raw: any): ProjectSummary {
  return {
    id: raw.id,
    projectName: raw.projectName || raw.setup?.projectName || "Untitled Project",
    clientName: raw.clientName || raw.setup?.clientName || "",
    distributorName: raw.distributorName || raw.setup?.distributorName || "",
    createdAt: raw.createdAt || new Date().toISOString(),
    bidDate: raw.bidDate || raw.setup?.bidDate || raw.createdAt?.split("T")[0] || new Date().toISOString().split("T")[0],
    endDate: raw.endDate || raw.projectEndEst || raw.setup?.projectEndEst || "",
    ownerId: raw.ownerId || "",
    thumbnail: raw.thumbnail || raw.setup?.thumbnail,
    latestVersionName: raw.latestVersionName,
    latestTotalCost: raw.latestTotalCost,
    currency: raw.currency || raw.setup?.currency || "GBP",
    status: normalizeProjectStatus(raw.status || raw.setup?.status || "Bidding"),
    themeAccentColor: normalizeHexColour(raw.themeAccentColor || raw.setup?.themeAccentColor) || generateProjectShowColour(raw.id || raw.projectName),
    themeAccentColorSource: raw.themeAccentColorSource || raw.setup?.themeAccentColorSource || "auto",
  };
}

function sortProjectSummaries(items: ProjectSummary[], sortBy: "date" | "alpha") {
  const next = [...items];
  if (sortBy === "date") {
    next.sort((a, b) => new Date(b.bidDate).getTime() - new Date(a.bidDate).getTime());
  } else {
    next.sort((a, b) => a.projectName.localeCompare(b.projectName));
  }
  return next;
}

export function ProjectDashboard({ onSelectProject, onNewProject }: Props) {
  const { user } = useAuth();
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingProjectId, setUploadingProjectId] = useState<string | null>(null);
  const uploadingProjectIdRef = useRef<string | null>(null);
  const [collapsedYears, setCollapsedYears] = useState<Record<string, boolean>>({});
  const [viewMode, setViewMode] = useState<"grid" | "compact" | "list">(() => {
    return (localStorage.getItem("dashboard_view_mode") as "grid" | "compact" | "list") || "grid";
  });
  const [sortBy, setSortBy] = useState<"date" | "alpha">(() => {
    return (localStorage.getItem("dashboard_sort_by") as "date" | "alpha") || "date";
  });
  const [groupBy, setGroupBy] = useState<"year" | "year-status">(() => {
    return (localStorage.getItem("dashboard_group_by") as "year" | "year-status") || "year";
  });
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem("dashboard_project_statuses");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const statusFilterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) return;
    try {
      const stored = localStorage.getItem(DASHBOARD_PROJECTS_CACHE_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return;
      const cachedProjects = sortProjectSummaries(parsed.map(normalizeProjectSummary), sortBy);
      if (cachedProjects.length > 0) {
        setProjects(cachedProjects);
        setLoading(false);
      }
    } catch (error) {
      console.warn("Failed to hydrate dashboard project cache", error);
    }
  }, [sortBy, user]);

  const toggleYear = (year: string) => {
    setCollapsedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  const toggleViewMode = (mode: "grid" | "compact" | "list") => {
    setViewMode(mode);
    localStorage.setItem("dashboard_view_mode", mode);
  };

  const toggleSortBy = () => {
    const nextSort = sortBy === "date" ? "alpha" : "date";
    setSortBy(nextSort);
    localStorage.setItem("dashboard_sort_by", nextSort);
  };

  const handleSetGroupBy = (nextGroupBy: "year" | "year-status") => {
    setGroupBy(nextGroupBy);
    localStorage.setItem("dashboard_group_by", nextGroupBy);
  };

  const handleToggleStatus = (status: string) => {
    setSelectedStatuses((current) => {
      const next = current.includes(status)
        ? current.filter((item) => item !== status)
        : [...current, status];
      localStorage.setItem("dashboard_project_statuses", JSON.stringify(next));
      return next;
    });
  };

  const projectAccent = "#e4a4a5";

  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const projectId = uploadingProjectIdRef.current;
    
    console.log("Thumbnail upload triggered:", { file: file?.name, projectId });
    
    if (!file || !projectId) {
      console.warn("Missing file or project ID for thumbnail upload");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = async () => {
        console.log("Image loaded, processing thumbnail...");
        const canvas = document.createElement("canvas");
        canvas.width = 500;
        canvas.height = 500;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Calculate crop to center the image as a square
        const size = Math.min(img.width, img.height);
        const x = (img.width - size) / 2;
        const y = (img.height - size) / 2;

        ctx.drawImage(img, x, y, size, size, 0, 0, 500, 500);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
        console.log("Thumbnail generated, sending to server...");

        try {
          const project = projects.find(p => p.id === projectId);
          const res = await fetch(`/api/projects/${projectId}`, {
            method: "PUT",
            headers: { 
              "Content-Type": "application/json",
              "x-user-uid": user?.uid || ""
            },
            body: JSON.stringify({ 
              thumbnail: dataUrl,
              "setup.thumbnail": dataUrl
            })
          });
          
          if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            throw new Error(errorData.error || "Failed to update thumbnail");
          }
          
          console.log("Thumbnail updated successfully");
          toast.success("Thumbnail updated");
          
          // Refresh projects list
          await fetchProjects();
        } catch (error: any) {
          console.error("Failed to update thumbnail:", error);
          toast.error(`Failed to update thumbnail: ${error.message}`);
        }
        
        setUploadingProjectId(null);
        uploadingProjectIdRef.current = null;
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      };
      img.src = event.target?.result as string;
    };
    reader.onerror = (err) => {
      console.error("FileReader error:", err);
      toast.error("Failed to read image file");
    };
    reader.readAsDataURL(file);
  };

  const fetchProjects = async () => {
    if (!user) {
      setLoading(false);
      setProjects([]);
      return;
    }

    try {
      let data: any[] = [];
      const res = await fetch("/api/projects?summary=1", {
        headers: { "x-user-uid": user?.uid || "" }
      });
      if (!res.ok) {
        throw new Error("Failed to load projects");
      }
      data = await res.json();
      
      const fetchedProjects = sortProjectSummaries(data.map(normalizeProjectSummary), sortBy);
      
      setProjects(fetchedProjects);
      try {
        localStorage.setItem(DASHBOARD_PROJECTS_CACHE_KEY, JSON.stringify(fetchedProjects));
      } catch (error) {
        console.warn("Failed to persist dashboard project cache", error);
      }
      
      const currentYear = new Date().getFullYear().toString();
      const years = Array.from(new Set(fetchedProjects.map(p => p.bidDate.split('-')[0])));
      setCollapsedYears(prev => {
        const newState = { ...prev };
        years.forEach(year => {
          if (newState[year] === undefined) {
            newState[year] = year !== currentYear;
          }
        });
        return newState;
      });
    } catch (error) {
      console.error("Failed to fetch projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [user, sortBy]);

  useEffect(() => {
    if (!user) {
      setCompanies([]);
      return;
    }

    const fetchCompanies = async () => {
      try {
        const res = await fetch("/api/crm/companies", {
          headers: { "x-user-uid": user.uid }
        });
        if (res.ok) {
          setCompanies(await res.json());
        }
      } catch (error) {
        console.warn("Failed to fetch dashboard company data", error);
      }
    };

    fetchCompanies();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target as Node)) {
        setStatusFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const companyByName = useMemo(() => {
    const map = new Map<string, any>();
    companies.forEach((company) => {
      const normalizedName = String(company?.name || "").trim().toLowerCase();
      if (normalizedName) {
        map.set(normalizedName, company);
      }
    });
    return map;
  }, [companies]);

  const [openingProjectId, setOpeningProjectId] = useState<string | null>(null);

  const availableStatuses = useMemo(() => {
    const statuses = Array.from(new Set(projects.map((project) => normalizeProjectStatus(project.status))));
    return statuses.sort(compareProjectStatuses);
  }, [projects]);

  const activeStatuses = selectedStatuses.length > 0 ? selectedStatuses : availableStatuses;

  const filteredProjects = useMemo(() => {
    if (activeStatuses.length === 0) return projects;
    return projects.filter((project) => activeStatuses.includes(normalizeProjectStatus(project.status)));
  }, [activeStatuses, projects]);

  const groupedProjects = useMemo(() => {
    const yearGroups = filteredProjects.reduce((acc, project) => {
      const endDate = project.endDate?.trim();
      const bucket = endDate ? new Date(`${endDate}T00:00:00`) : null;
      const yearKey = bucket && !Number.isNaN(bucket.getTime())
        ? String(bucket.getFullYear())
        : "no-end-date";
      if (!acc[yearKey]) acc[yearKey] = [];
      acc[yearKey].push(project);
      return acc;
    }, {} as Record<string, ProjectSummary[]>);

    return (Object.entries(yearGroups) as [string, ProjectSummary[]][])
      .sort(([groupA], [groupB]) => {
        if (groupA === "no-end-date") return 1;
        if (groupB === "no-end-date") return -1;
        return parseInt(groupB, 10) - parseInt(groupA, 10);
      })
      .map(([yearKey, yearProjects]) => ({
        yearKey,
        yearLabel: yearKey === "no-end-date" ? "No End Date" : yearKey,
        projects: yearProjects,
        statusGroups: (Object.entries(
          yearProjects.reduce((acc, project) => {
            const statusKey = normalizeProjectStatus(project.status);
            if (!acc[statusKey]) acc[statusKey] = [];
            acc[statusKey].push(project);
            return acc;
          }, {} as Record<string, ProjectSummary[]>)
        ) as [string, ProjectSummary[]][]).sort(([left], [right]) => compareProjectStatuses(left, right)),
      }));
  }, [filteredProjects]);

  const handleProjectClick = async (projectId: string) => {
    if (openingProjectId) return;
    setOpeningProjectId(projectId);
    try {
      await onSelectProject(projectId);
    } finally {
      setOpeningProjectId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col pb-12">
      <div className="bg-[#fcf1f1] dark:bg-[#302122] px-6 py-4 rounded-lg border border-[#efcfd0] dark:border-[#6a4446] flex items-center justify-between mb-6 shadow-sm shrink-0">
        <div className="flex flex-col">
          <h2 className="text-2xl font-brandon font-medium text-slate-900 dark:text-slate-100">Projects</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Manage and track all your active bidding projects</p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={groupBy}
            onChange={(event) => handleSetGroupBy(event.target.value as "year" | "year-status")}
            className="rounded-lg border border-[#efcfd0] bg-white/80 px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm outline-none transition focus:border-[#e4a4a5] dark:border-[#6a4446] dark:bg-slate-900 dark:text-slate-200"
            title="Group projects"
          >
            <option value="year">Group: End Year</option>
            <option value="year-status">Group: End Year + Status</option>
          </select>
          <div className="relative" ref={statusFilterRef}>
            <button
              type="button"
              onClick={() => setStatusFilterOpen((current) => !current)}
              className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors dark:text-slate-200"
              style={{
                backgroundColor: "rgba(228, 164, 165, 0.18)",
                borderColor: "rgba(228, 164, 165, 0.55)",
              }}
            >
              <Filter className="w-3.5 h-3.5" />
              {selectedStatuses.length > 0 ? `Status (${selectedStatuses.length})` : "All Statuses"}
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {statusFilterOpen ? (
              <div className="absolute right-0 z-20 mt-2 w-60 rounded-xl border border-slate-200 bg-white p-2 shadow-xl dark:border-slate-800 dark:bg-slate-900">
                <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Project Status
                </div>
                {availableStatuses.map((status) => {
                  const checked = activeStatuses.includes(status);
                  return (
                    <label
                      key={status}
                      className="flex cursor-pointer items-center justify-between rounded-lg px-2 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <span>{status}</span>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleToggleStatus(status)}
                        className="h-4 w-4 rounded border-slate-300 text-[#e4a4a5] focus:ring-[#e4a4a5]"
                      />
                    </label>
                  );
                })}
              </div>
            ) : null}
          </div>
          <button
            onClick={toggleSortBy}
            className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors dark:text-slate-200"
            style={{
              backgroundColor: "rgba(228, 164, 165, 0.18)",
              borderColor: "rgba(228, 164, 165, 0.55)",
            }}
            title={`Sorting by ${sortBy === "date" ? "Bid Date" : "Alphabetical"}`}
          >
            <ArrowUpDown className="w-3.5 h-3.5" />
            {sortBy === "date" ? "Sort: Date" : "Sort: A-Z"}
          </button>
          <div
            className="flex items-center rounded-lg border p-1"
            style={{
              backgroundColor: "rgba(228, 164, 165, 0.16)",
              borderColor: "rgba(228, 164, 165, 0.5)",
            }}
          >
            <button
              onClick={() => toggleViewMode("compact")}
              className={cn(
                "p-1.5 rounded-md transition-all",
                viewMode === "compact"
                  ? "shadow-sm text-white"
                  : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              )}
              style={viewMode === "compact" ? { backgroundColor: projectAccent } : undefined}
              title="Dense View"
            >
              <span className="grid grid-cols-3 gap-[2px]">
                {Array.from({ length: 9 }).map((_, index) => (
                  <span
                    key={index}
                    className="block h-[3px] w-[3px] rounded-[1px] bg-current"
                  />
                ))}
              </span>
            </button>
            <button
              onClick={() => toggleViewMode("grid")}
              className={cn(
                "p-1.5 rounded-md transition-all",
                viewMode === "grid"
                  ? "shadow-sm text-white"
                  : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              )}
              style={viewMode === "grid" ? { backgroundColor: projectAccent } : undefined}
              title="Grid View"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => toggleViewMode("list")}
              className={cn(
                "p-1.5 rounded-md transition-all",
                viewMode === "list"
                  ? "shadow-sm text-white"
                  : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              )}
              style={viewMode === "list" ? { backgroundColor: projectAccent } : undefined}
              title="List View"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={onNewProject}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#ffcc01] text-slate-900 text-xs font-medium rounded-lg hover:bg-[#e6b801] transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Create Project
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto pr-2">
        {projects.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 p-12 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 text-center max-w-2xl mx-auto mt-8 transition-colors duration-300">
            <div className="w-16 h-16 bg-indigo-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Film className="w-8 h-8 text-indigo-400 dark:text-indigo-500" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">No projects yet</h3>
            <p className="text-slate-500 dark:text-slate-400 mb-8">
              Create your first project to start bidding on shots and assets.
            </p>
            <button
              onClick={onNewProject}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#ffcc01] text-slate-900 text-xs font-medium rounded-lg hover:bg-[#e6b801] transition-colors mx-auto"
            >
              <Plus className="w-4 h-4" />
              Create Project
            </button>
          </div>
        ) : (
          <>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleThumbnailUpload}
              accept="image/*"
              className="hidden"
            />
            
            <div className="space-y-12 pb-8">
            {groupedProjects.map(({ yearKey: groupKey, yearLabel: groupLabel, projects: groupedProjects, statusGroups }) => {
              return (
              <div key={groupKey} className="space-y-4">
                <button 
                  onClick={() => toggleYear(groupKey)}
                  className="flex items-center gap-3 group w-full text-left"
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 group-hover:bg-indigo-50 dark:group-hover:bg-slate-700 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 transition-colors">
                    {collapsedYears[groupKey] ? <ChevronRight className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-slate-400 dark:text-slate-500" />
                    <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">{groupLabel}</h3>
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-xs font-medium">
                      {groupedProjects.length} {groupedProjects.length === 1 ? 'Project' : 'Projects'}
                    </span>
                  </div>
                  <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800 ml-4"></div>
                </button>

                {!collapsedYears[groupKey] && (
                  <div className="space-y-5">
                    {(groupBy === "year-status" ? statusGroups : [["all", groupedProjects] as [string, ProjectSummary[]]])
                      .map(([statusLabel, statusProjects]) => (
                        <div key={`${groupKey}-${statusLabel}`} className="space-y-3">
                          {groupBy === "year-status" ? (
                            <div className="flex items-center gap-3 pl-11">
                              <span
                                className={cn(
                                  "rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] leading-none",
                                  getProjectStatusTone(statusLabel),
                                )}
                              >
                                {statusLabel}
                              </span>
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                {statusProjects.length} {statusProjects.length === 1 ? "project" : "projects"}
                              </span>
                              <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
                            </div>
                          ) : null}
                          <div className={cn(
                            viewMode === "grid"
                              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                              : viewMode === "compact"
                                ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4"
                                : "grid grid-cols-1 2xl:grid-cols-2 gap-3"
                          )}>
                    {statusProjects.map((project) => (
                      <div
                        key={project.id}
                        onClick={() => handleProjectClick(project.id)}
                        className={cn(
                          "bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-500 transition-all cursor-pointer group flex relative",
                          viewMode === "grid"
                            ? "flex-row h-36"
                            : viewMode === "compact"
                              ? "flex-row h-[104px]"
                              : "flex-row-reverse h-[72px] w-full",
                          openingProjectId === project.id && "opacity-60 pointer-events-none"
                        )}
                      >
                        {(() => {
                          const productionCompany = companyByName.get(String(project.clientName || "").trim().toLowerCase()) || null;
                          const distributorCompany = companyByName.get(String(project.distributorName || "").trim().toLowerCase()) || null;
                          const productionLogoUrl = getCompanyLogoUrl(productionCompany);
                          const distributorLogoUrl = getCompanyLogoUrl(distributorCompany);

                          return (
                            <>
                        {openingProjectId === project.id && (
                          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/40 dark:bg-slate-900/40 backdrop-blur-[1px]">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                          </div>
                        )}
                        <div className={cn(
                          "relative p-4 flex-1 flex flex-col min-w-0",
                          viewMode === "list" && "flex-row items-center justify-between py-0",
                          viewMode === "compact" && "px-4 py-3"
                        )}>
                          <div className={cn(
                            "flex flex-col min-w-0",
                            viewMode === "list" && "flex-row items-center gap-4 flex-1"
                          )}>
                            <div className="flex items-center gap-2 min-w-0">
                              <h3 className={cn(
                                "font-brandon text-lg font-medium text-slate-900 dark:text-slate-100 leading-tight",
                                viewMode === "list" ? "truncate" : "whitespace-normal",
                                viewMode === "compact" && "text-[1.05rem] leading-tight truncate whitespace-nowrap"
                              )}
                              title={viewMode === "compact" ? project.projectName : undefined}>
                                {project.projectName}
                              </h3>
                              {viewMode !== "compact" ? (
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                  {normalizeProjectStatus(project.status)}
                                </span>
                              ) : null}
                            </div>
                            <div className={cn(
                              "flex items-center gap-2 min-w-0",
                              viewMode === "grid" ? "mb-1" : "mb-0",
                              viewMode === "compact" && "mt-0.5"
                            )}>
                              <p className={cn(
                                "text-slate-500 dark:text-slate-400 truncate",
                                viewMode === "compact" ? "text-[13px]" : "text-sm"
                              )}>
                                {project.clientName || "No client specified"}
                              </p>
                              {project.latestVersionName && viewMode !== "compact" && (
                                <span className="text-[10px] font-bold uppercase tracking-wider whitespace-nowrap text-indigo-600 dark:text-indigo-400 shrink-0">
                                  {project.latestVersionName}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className={cn(
                            "flex items-center gap-4",
                            viewMode === "grid" ? "mt-auto" : "ml-auto",
                            viewMode === "compact" && "mt-auto ml-0 gap-3 justify-between"
                          )}>
                            <div className={cn(
                              "flex items-center gap-4",
                              viewMode === "compact" && "min-w-0"
                            )}>
                              {project.latestTotalCost !== undefined && (
                                <div className={cn(
                                  "text-indigo-600 font-bold",
                                  viewMode === "compact" ? "text-[13px]" : "text-sm"
                                )}>
                                  {formatCurrency(project.latestTotalCost, project.currency || "GBP")}
                                </div>
                              )}
                              <div className={cn(
                                "flex items-center gap-1.5 text-slate-400 dark:text-slate-500",
                                viewMode === "compact" ? "text-[11px]" : "text-xs"
                              )}>
                                <Calendar className="w-3.5 h-3.5" />
                                {new Date(project.bidDate).toLocaleDateString()}
                              </div>
                            </div>
                            {viewMode !== "compact" && (productionLogoUrl || distributorLogoUrl) && (
                              <div className={cn(
                                "ml-auto flex items-center",
                                "gap-2"
                              )}>
                                {productionLogoUrl && (
                                  <img
                                    src={productionLogoUrl}
                                    alt=""
                                    className={cn(
                                      "shrink-0 opacity-75",
                                      "h-[20px] w-[20px]"
                                    )}
                                    referrerPolicy="no-referrer"
                                  />
                                )}
                                {distributorLogoUrl && (
                                  <img
                                    src={distributorLogoUrl}
                                    alt=""
                                    className={cn(
                                      "shrink-0 opacity-75",
                                      "h-[20px] w-[20px]"
                                    )}
                                    referrerPolicy="no-referrer"
                                  />
                                )}
                              </div>
                            )}
                            {viewMode === "compact" && (productionLogoUrl || distributorLogoUrl) && (
                              <div className="ml-auto flex items-center gap-1.5 pr-0.5">
                                {productionLogoUrl && (
                                  <img
                                    src={productionLogoUrl}
                                    alt=""
                                    className="h-[16px] w-[16px] shrink-0 opacity-90"
                                    referrerPolicy="no-referrer"
                                  />
                                )}
                                {distributorLogoUrl && (
                                  <img
                                    src={distributorLogoUrl}
                                    alt=""
                                    className="h-[16px] w-[16px] shrink-0 opacity-90"
                                    referrerPolicy="no-referrer"
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div
                          className={cn(
                          "relative flex-shrink-0",
                          viewMode === "grid"
                            ? "w-36 h-36 border-l border-slate-200 dark:border-slate-800"
                            : viewMode === "compact"
                              ? "w-[104px] h-[104px] border-l border-slate-200 dark:border-slate-800"
                              : "w-[72px] h-[72px] border-r border-slate-200 dark:border-slate-800"
                          )}
                          style={{ backgroundColor: project.thumbnail ? undefined : project.themeAccentColor }}
                        >
                          {project.thumbnail ? (
                            <img 
                              src={project.thumbnail} 
                              alt={project.projectName} 
                              className="w-full h-full object-cover"
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Film className={cn(
                                "opacity-90",
                                viewMode === "grid" ? "w-10 h-10" : viewMode === "compact" ? "w-8 h-8" : "w-6 h-6"
                              )} style={{ color: getReadableTextColour(project.themeAccentColor || "#64748b") }} />
                            </div>
                          )}
                          
                          <div className="absolute bottom-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                uploadingProjectIdRef.current = project.id;
                                setUploadingProjectId(project.id);
                                fileInputRef.current?.click();
                              }}
                              className="p-1.5 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white dark:hover:bg-slate-700 transition-all"
                              title="Upload Thumbnail"
                            >
                              <Upload className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                            </>
                          );
                        })()}
                      </div>
                    ))}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )})}
          </div>
        </>
      )}
      </div>
    </div>
  );
}
